import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import TokenAvatar from '@/widget/TokenAvatar';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import TradeHead from './TradeHead';
import TradeRow from './TradeRow';
import NoData from '@/widget/NoData';
import { formatSmartWalletBoughtMessage, formatTimeAgo } from '@/lib/format';
import { useAppStore } from '@/store';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import { IPriceUpdate } from '../SignalCard';
import BigNumber from 'bignumber.js';
interface IProps {
  tokenInfo: {
    logo: string;
    address: string;
    symbol: string;
    createTime: number;
    timeRange: number;
    tradeCount: number;
    totalSupply: number;
    initPrice: string;
  };
  tradeList: {
    amount: string;
    bought: string;
    price: string;
    timestamp: number;
    walletAddress: string;
  }[];
}
export default (props: IProps) => {
  const { tradeList, tokenInfo } = props;
  const { lang } = useAppStore();
  const [open, setOpen] = useState(false);

  const { subscribe } = useAppEventBusContext();

  const [currentPrice, setCurrentPrice] = useState<string>(tokenInfo?.initPrice);

  useEffect(() => {
    const unsubscribe = subscribe(SubscribeEvent.Update_Signal_Price, (data: IPriceUpdate) => {
      if (data.address.toLowerCase() == tokenInfo?.address.toLowerCase()) {
        setCurrentPrice(data.price);
      }
    });
    return () => unsubscribe();
  }, [subscribe, tokenInfo?.address]);

  const renderList = useMemo(() => {
    return tradeList
      .map((item) => {
        const marketCap = BigNumber(currentPrice).times(tokenInfo?.totalSupply).toString();
        const priceChg = BigNumber(currentPrice).minus(item.price).div(item.price).toString();
        const profit = BigNumber(item.amount).times(currentPrice).minus(item.bought).toString();
        return {
          ...item,
          marketCap,
          priceChg,
          profit,
        };
      })
      .sort((a, b) => BigNumber(b.profit).minus(a.profit).toNumber());
  }, [tradeList, currentPrice, tokenInfo?.totalSupply]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger>
        <div className="flex items-center gap-x-[6px] mt-[6px]">
          <img src="/icon/signal-wallet.svg" className="size-[14px]" />
          <div className="relative">
            <span
              className="text-[14px] text-transparent bg-clip-text"
              style={{
                backgroundImage: 'linear-gradient(to right, #FF8C00, #ECBB21, #31E46C, #0ECB81)',
              }}
            >
              {formatSmartWalletBoughtMessage(tokenInfo?.tradeCount, tokenInfo?.timeRange, lang)}
            </span>
            <div
              className="w-full h-[0.7px] absolute bottom-[4px]"
              style={{
                backgroundImage: 'linear-gradient(to right, #FF8C00, #ECBB21, #31E46C, #0ECB81)',
              }}
            ></div>
          </div>
        </div>
      </SheetTrigger>
      <SheetContent
        side="bottom"
        hideCloseIcon
        className="p-[16px] border-t-[1px] border-t-borderColor rounded-tl-[12px] rounded-tr-[12px] outline-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SheetHeader className="hidden" />
        <SheetTitle className="hidden" />
        <SheetDescription className="hidden" />
        <div className="flex flex-col gap-y-[16px]  h-[500px] overflow-auto">
          <div className="flex justify-between">
            <div className="flex items-center gap-x-[12px]">
              <TokenAvatar className="size-[25px]" address={tokenInfo?.address} logo={tokenInfo?.logo} />
              <div>
                <div>{tokenInfo?.symbol}</div>
                <span className="text-secondaryTextColor text-[12px]">{formatTimeAgo(tokenInfo?.createTime, { style: 'shortAgo' })}</span>
              </div>
            </div>
            <X className="size-[16px]" onClick={() => setOpen(false)} />
          </div>
          <div className="flex items-center gap-x-[6px] mt-[6px]">
            <img src="/icon/signal-wallet.svg" className="size-[14px]" />
            <div className="relative">
              <span
                className="text-[16px] text-transparent bg-clip-text"
                style={{
                  backgroundImage: 'linear-gradient(to right, #FF8C00, #ECBB21, #31E46C, #0ECB81)',
                }}
              >
                {formatSmartWalletBoughtMessage(tokenInfo?.tradeCount, tokenInfo?.timeRange, lang)}
              </span>
              <div
                className="w-full h-[0.7px] absolute bottom-[4px]"
                style={{
                  backgroundImage: 'linear-gradient(to right, #FF8C00, #ECBB21, #31E46C, #0ECB81)',
                }}
              ></div>
            </div>
          </div>
          <TradeHead />
          {renderList?.length > 0 ? (
            <div className="h-[400px] overflow-y-auto">
              {renderList.map((item, index) => {
                return <TradeRow data={item} key={item.walletAddress + index} />;
              })}
            </div>
          ) : (
            <NoData />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
